<template>
<div>
    <!-- modal -->
    <b-modal id="invoiceReminderModal" centered size="lg" no-close-on-backdrop ref="invoiceReminderModal" :title="reminderId === 0 ? $t('invoiceReminder.add_reminder') : $t('invoiceReminder.edit_reminder')" :cancel-title="$t('others.cancel_title')" :ok-title="$t('others.ok_title')" cancel-variant="outline-secondary" @ok.prevent="addOrUpdate">
        <validation-observer ref="reminderRules">
            <b-form>
                <b-row>
                    <b-col class="col-lg-6">
                        <b-form-group>
                            <label>{{ $t('invoiceReminder.number') }}</label>
                            <b-form-input v-model="currentReminder.reminderNumber" size="sm" disabled />
                        </b-form-group>
                    </b-col>
                    <b-col class="col-lg-6">
                        <b-form-group>
                            <label>{{ $t('invoiceReminder.send_date') }}</label>
                            <validation-provider v-slot="{ errors }" :name="$t('invoiceReminder.send_date')" rules="required">
                                <CustomDatePicker :datePickerValidateOption="0" v-model="currentReminder.reminderSendDate" :state="errors.length > 0 ? false : null"></CustomDatePicker>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col class="col-lg-6">
                        <b-form-group>
                            <label>{{ $t('invoiceReminder.employee') }}</label>
                            <validation-provider v-slot="{ errors }" :name="$t('invoiceReminder.employee')" rules="required|excluded:0">
                                <b-form-select v-model="currentReminder.invoiceResponsibleEmployeeId" :options="employeeList" value-field="id" text-field="name" size="sm" :state="errors.length > 0 ? false : null" />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col class="col-lg-6">
                        <b-form-group>
                            <label>{{ $t('invoiceReminder.response_date') }}</label>
                            <CustomDatePicker :datePickerValidateOption="0" v-model="currentReminder.agentResponseDate"></CustomDatePicker>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col class="col-lg-12">
                        <b-form-group>
                            <label>{{ $t('invoiceReminder.note') }}</label>
                            <validation-provider v-slot="{ errors }" :name="$t('invoiceReminder.note')">
                                <b-form-textarea v-model="currentReminder.note" rows="3" maxlength="250" :state="errors.length > 0 ? false : null" />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                </b-row>
            </b-form>
        </validation-observer>
        <template #modal-footer v-if="reminderId > 0">
            <b-button variant="danger" @click="deleteReminder">
                {{ $t('others.delete') }}
            </b-button>
        </template>
    </b-modal>
</div>
</template>

<script>
export default {
    props: {
        getInvoiceReminderListByInvoiceId: {
            type: Function,
            required: true
        }
    },
    data() {
        return {
            reminderId: 0,
            invoiceId: 0,
            employeeList: [],
            currentReminder: {
                id: 0,
                invoiceId: 0,
                reminderNumber: 0,
                note: '',
                reminderSendDate: null,
                agentResponseDate: null,
                invoiceResponsibleEmployeeId: 0
            }
        }
    },
    methods: {
        clear() {
            this.reminderId = 0
            this.invoiceId = 0
            this.currentReminder = {
                id: 0,
                invoiceId: 0,
                reminderNumber: 0,
                note: '',
                reminderSendDate: null,
                agentResponseDate: null,
                invoiceResponsibleEmployeeId: 0
            }
        },
        showInfo(reminderId, invoiceId) {
            this.reminderId = reminderId
            this.invoiceId = invoiceId
            this.currentReminder.invoiceId = invoiceId

            this.$nextTick(() => {
                if (this.$refs.invoiceReminderModal) {
                    this.$refs.invoiceReminderModal.show()
                }
            })

            const data = {
                id: reminderId
            }
            const controller = {
                name: 'InvoiceReminder',
                actionName: 'GetInvoiceReminderInfo'
            }
            const payload = {
                data,
                controller
            }
            this.$store.dispatch('moduleAuth/customService', payload).then((response) => {
                if (response.data.resultStatus) {
                    // Load employee list from response
                    this.employeeList = response.data.employeeList || []
                    if (!this.employeeList.some(e => e.id === 0)) {
                        this.employeeList.splice(0, 0, this.$nullSelected())
                    }
                    // Load reminder data if editing
                    if (response.data.invoiceReminder.id > 0) {
                        const reminder = response.data.invoiceReminder
                        this.currentReminder = {
                            id: reminder.id,
                            invoiceId: reminder.invoiceId,
                            reminderNumber: reminder.reminderNumber,
                            note: reminder.note,
                            reminderSendDate: reminder.reminderSendDate,
                            agentResponseDate: reminder.agentResponseDate,
                            invoiceResponsibleEmployeeId: reminder.invoiceResponsibleEmployeeId
                        }
                    } else {
                        // For new reminder, calculate next reminder number
                        this.getNextReminderNumber()
                    }
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                }
            })
        },
        getNextReminderNumber() {
            const data = {
                id: this.invoiceId
            }
            const controller = {
                name: 'InvoiceReminder',
                actionName: 'GetInvoiceReminderListByInvoiceId'
            }
            const payload = {
                data,
                controller
            }
            this.$store.dispatch('moduleAuth/customService', payload).then((response) => {
                if (response.data.resultStatus) {
                    const reminders = (response.data.invoiceReminderList || []).filter(r => !r.isDeleted)
                    this.currentReminder.reminderNumber = reminders.length + 1
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                }
            })
        },
        addOrUpdate() {
            this.$refs.reminderRules.validate().then(success => {
                if (success) {
                    const data = {
                        id: this.currentReminder.id,
                        invoiceId: this.invoiceId,
                        reminderNumber: this.currentReminder.reminderNumber,
                        note: this.currentReminder.note,
                        reminderSendDate: this.currentReminder.reminderSendDate,
                        agentResponseDate: this.currentReminder.agentResponseDate,
                        invoiceResponsibleEmployeeId: this.currentReminder.invoiceResponsibleEmployeeId
                    }
                    const controller = {
                        name: 'InvoiceReminder',
                        actionName: 'SaveInvoiceReminder'
                    }
                    const payload = {
                        data,
                        controller
                    }

                    this.$store.dispatch('moduleAuth/save', payload).then((response) => {
                        if (response.data.resultStatus) {
                            this.$SaveAlert()
                            this.$bvModal.hide('invoiceReminderModal')
                            this.getInvoiceReminderListByInvoiceId()
                            this.clear()
                        } else {
                            this.$CustomWarningAlert(response.data.resultMessage)
                        }
                    })
                }
            })
        },
        deleteReminder() {
            this.$DeleteConfirm().then(result => {
                if (result.value) {
                    const data = {
                        id: this.currentReminder.id
                    }
                    const controller = {
                        name: 'InvoiceReminder'
                    }
                    const payload = {
                        data,
                        controller
                    }

                    this.$store.dispatch('moduleAuth/delete', payload).then((response) => {
                        if (response.data.resultStatus) {
                            this.$DeleteAlert()
                            this.$bvModal.hide('invoiceReminderModal')
                            this.getInvoiceReminderListByInvoiceId()
                            this.clear()
                        } else {
                            this.$WarningAlert(response.data.resultMessage)
                        }
                    })
                }
            })
        }
    }
}
</script>

<style lang="scss" scoped>
</style>
